'use client';

import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Skeleton } from '@/components/common/feedback/skeleton/skeleton';
import { Card } from '@/components/common/layout/card';

export const AIToolsBanner = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="relative w-full space-y-0"
      >
        <div className="flex items-center gap-2 p-3 sm:p-4">
          <Skeleton className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="space-y-1">
            <Skeleton className="h-4 w-36 bg-gray-200 dark:bg-gray-700" />
            <Skeleton className="h-3 w-60 bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
        <Card className="relative overflow-hidden border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800/50 p-3 sm:p-4">
          <div className="flex items-start justify-between">
            <div className="w-full space-y-2">
              <Skeleton className="mb-2 h-7 w-44 bg-gray-200 dark:bg-gray-700" />
              <Skeleton className="mb-2 h-5 w-60 bg-gray-200 dark:bg-gray-700" />
              <Skeleton className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700" />
            </div>
            <Skeleton className="absolute right-0 top-0 h-full w-1/2 bg-gray-200 dark:bg-gray-700" />
          </div>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="relative w-full space-y-3 sm:space-y-4"
    >
      {/* AI Tools Header */}
      <motion.div
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4"
      >
        <div className="relative">
          <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-500 to-purple-300 dark:from-purple-600 dark:to-purple-400 opacity-75 blur-lg" />
          <div className="relative rounded-full bg-purple-500 dark:bg-purple-600 p-1.5 sm:p-2">
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
          </div>
        </div>
        <div className="space-y-0.5 sm:space-y-1">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">AI Tools</h2>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Use our AI tools for more efficiency on finding lawyers
          </p>
        </div>
        <Link href="/ai-tools">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="transition-transform group-hover:translate-x-0.5 text-gray-700 dark:text-gray-300"
            >
              <path
                d="M1.33325 8H14.6666M14.6666 8L7.99992 1.33334M14.6666 8L7.99992 14.6667"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.button>
        </Link>
      </motion.div>

      {/* AI-Powered Legal Docs Card */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex items-center justify-between border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800/50 p-3 sm:p-4 md:p-6 rounded-lg"
      >
        <div className="w-full space-y-2 sm:space-y-3">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
            AI-Powered Legal Docs
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Use our ai tool to help draft your legal documents.
          </p>
        </div>
        <div className="relative h-full w-1/2">
          <Image
            src="/images/AIBanner.png"
            alt="AI Legal Documents Interface"
            className="w-full max-w-[160px] sm:max-w-[200px] dark:opacity-90 dark:contrast-125 dark:brightness-90"
            width={200}
            height={200}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};
